// =============================================================================
// Economy: Energy, Coins, Gems, IAP, Ads
// =============================================================================

export const ENERGY = {
  /**
   * Maximum energy capacity (regen ceiling — paid grants still overflow, see
   * `07-game-design-rules.md` §"Energy Cap Scope").
   *
   * Raised 5 → 10 (2026-08-10). At a ~2 min match, a cap of 5 ended a new
   * player's first session after ~10 minutes — the point at which a word game
   * is just starting to click, and long before anyone would consider paying.
   * This is the FAILSAFE default only; the live value is
   * `AppConfig.economy.maxEnergy` (admin-tunable).
   */
  MAX: 10,
  /** Energy starting amount for new players. Raised 5 → 10 with MAX. */
  STARTING: 10,
  /** Energy cost to start a match */
  COST_PER_MATCH: 1,
  /**
   * Passive regeneration interval in minutes. Lowered 20 → 10 (2026-08-10):
   * refilling a 10-cap at 20 min/point took over three hours from empty.
   * Failsafe default only — live value is
   * `AppConfig.economy.regenIntervalMinutes`.
   */
  REGEN_INTERVAL_MINUTES: 10,
  /** Passive regeneration interval in milliseconds. Keep in sync with above. */
  REGEN_INTERVAL_MS: 10 * 60 * 1000,
  /** Energy gained from watching a rewarded ad */
  AD_REWARD_AMOUNT: 2,
  /**
   * Maximum rewarded ad watches per day for energy.
   * Economy Tuning v1: lowered from 5 → 3 to prevent ad-grinding from
   * trivialising the energy gate (see `docs/ECONOMY.md` §10).
   */
  MAX_AD_WATCHES_PER_DAY: 3,
  /**
   * Client mirror only: how long after an optimistic local energy write (Play's
   * deduction, a cancel's refund) the mobile store refuses a server re-sync.
   * The local write races the socket event that makes the server agree, so a
   * profile read inside this window can predate it.
   */
  OPTIMISTIC_SYNC_GRACE_MS: 5_000,
} as const;

export const COINS = {
  /** Starting coins for new players */
  STARTING: 200,
  /** Coins earned on match win */
  MATCH_WIN_REWARD: 50,
  /** Coins earned on match loss */
  MATCH_LOSS_REWARD: 10,
  /** Coins earned from watching a rewarded ad */
  AD_REWARD_AMOUNT: 50,
  /**
   * Maximum rewarded ad watches per day for coins.
   * Economy Tuning v1: lowered from 5 → 3 (matches energy cap) so the
   * day's coin earn from ads is bounded (see `docs/ECONOMY.md` §10).
   */
  MAX_AD_WATCHES_PER_DAY: 3,
  /** Daily bonus coins (legacy 5-rounds challenge) */
  DAILY_BONUS_AMOUNT: 100,
  /**
   * @deprecated Economy Tuning v1 replaces the rolling 8-hour reset with
   * a UTC-daily reset (see `docs/ECONOMY.md` §4.1, §4.3). Sub-issue #415
   * reads `lastDailyBonusClaimedAt` / `dailyChallengeClaimedAt` and the
   * server clock instead. Kept here only so unrelated imports don't fail
   * to compile during the staged rollout — DO NOT add new readers.
   */
  DAILY_BONUS_RESET_HOURS: 8,
  /**
   * Wins required in a UTC day to fire the daily-challenge bonus.
   *
   * Lowered 5 → 3 (2026-08-10). At a ~50% win rate 5 wins costs ~10 matches;
   * paired with the old 5-energy cap that was not completable in one session,
   * so most players never saw the bonus at all. Failsafe default only — the
   * live value is `AppConfig.economy.dailyChallengeRequiredWins`.
   */
  DAILY_BONUS_REQUIRED_WINS: 3,
  /**
   * 7-day daily-login bonus curve (Economy Tuning v1, `docs/ECONOMY.md`
   * §4.1). Index = (streak day - 1). Day 7 also grants
   * {@link GEMS.DAILY_LOGIN_BONUS_DAY7} on top of the coin reward.
   * After day 7 the curve loops back to index 0.
   */
  DAILY_LOGIN_BONUS_CURVE: [50, 75, 100, 150, 200, 300, 500] as const,
  /**
   * Coins granted for the first match win of the UTC day. Stacks with
   * the standard match-win reward and the answer bonus. See
   * `docs/ECONOMY.md` §4.2.
   */
  FIRST_WIN_OF_DAY_REWARD: 50,
  /**
   * Toggle for the post-match "watch ad to double rewards" affordance
   * (Economy Tuning v1, §4.4). Server-side enforcement lands in #416.
   */
  MATCH_REWARD_DOUBLER_AD_ENABLED: true,
} as const;

/**
 * Table gifts (#793): a coin-priced, purely cosmetic gift sent to any seat at
 * the table — another player or yourself — that shows on the recipient's
 * seat for {@link GIFTS.DISPLAY_MS}. A coin SINK only: the recipient gains
 * nothing spendable, so gifts can never move coins between accounts (no
 * farming, no laundering through a friend or a bot). [TUNING] prices.
 */
export const GIFTS = {
  /** Stable wire ids + coin price. NEVER rename or remove an id; retire one
   *  by dropping it from the picker only. Glyphs live in the mobile shell. */
  CATALOG: [
    { id: 'rose', price: 10 },
    { id: 'tea', price: 20 },
    { id: 'coffee', price: 30 },
    { id: 'cake', price: 50 },
    { id: 'trophy', price: 100 },
    { id: 'crown', price: 250 },
  ] as const,
  /** How long a gift stays on the recipient's seat — about a hand of play. */
  DISPLAY_MS: 90_000,
  /** How long the recipient's "X sent you a Rose" celebration stays up. */
  RECEIVED_CELEBRATION_MS: 2_800,
  /** Per-user send budget (socket window AND Redis quota). */
  SEND_RATE_LIMIT_PER_MINUTE: 6,
} as const;

export type GiftId = (typeof GIFTS.CATALOG)[number]['id'];

/** Every gift id, for the server's Zod enum. */
export const GIFT_IDS = GIFTS.CATALOG.map((g) => g.id) as [GiftId, ...GiftId[]];

/** The coin price of a gift id (server debit and client label read the same table). */
export function giftPrice(id: GiftId): number {
  const gift = GIFTS.CATALOG.find((g) => g.id === id);
  if (!gift) throw new Error(`Unknown gift id: ${id}`);
  return gift.price;
}

export const GEMS = {
  /** Starting gems for new players */
  STARTING: 50,
  /**
   * Gems granted on top of the coin reward when the daily-login bonus
   * streak hits day 7 (`docs/ECONOMY.md` §4.1).
   */
  DAILY_LOGIN_BONUS_DAY7: 10,
} as const;

/**
 * Daily-login bonus eligibility / streak windows (Economy Tuning v1,
 * `docs/ECONOMY.md` §4.1). Both are measured against
 * `User.lastDailyBonusClaimedAt`.
 *
 * - `ELIGIBILITY_HOURS`: minimum hours since last claim before the next
 *   day's bonus becomes claimable.
 * - `STREAK_BREAK_HOURS`: hours since last claim that resets the streak
 *   to 1. Anything in between (eligible but under the streak-break
 *   threshold) advances the streak by 1.
 */
export const DAILY_LOGIN_BONUS_ELIGIBILITY_HOURS = 20;
export const DAILY_LOGIN_BONUS_STREAK_BREAK_HOURS = 30;

/** Gem → Energy exchange rates */
export const GEM_TO_ENERGY_PACKS = [
  { energy: 10, gemCost: 30, productId: 'energy_10' },
  { energy: 30, gemCost: 80, productId: 'energy_30' },
  { energy: 80, gemCost: 200, productId: 'energy_80' },
] as const;

/** Gem → Coin exchange rates */
export const GEM_TO_COIN_PACKS = [
  { coins: 120, gemCost: 20, productId: 'coins_120' },
  { coins: 280, gemCost: 40, productId: 'coins_280' },
  { coins: 640, gemCost: 80, productId: 'coins_640' },
  { coins: 2000, gemCost: 200, productId: 'coins_2000' },
] as const;

/**
 * Gem IAP pricing.
 *
 * Values authoritative per `docs/ECONOMY.md` §3.2 (SKU table) and §10
 * (Deltas vs. existing constants). The `bestValue` flag is the
 * client-renderable hint for the value-anchor "Best Value" badge — by
 * design it sits ONLY on `gems_medium` (the price-anchor SKU).
 *
 * Non-gem SKUs live alongside the gem packs because `iapService.findProduct`
 * walks this single array — `gems: 0` is the discriminator. The grant
 * dispatch in `iapService.verifyAndGrant` switches on `productId` to route
 * to the correct grant function (gems vs. unlimited energy vs. permanent
 * "no ads" vs. starter bundle). When adding a new non-gem SKU, the grant
 * path is the extension point — not a parallel catalog.
 *
 * Non-gem SKU index:
 *   - `unlimited_24h`     — 24h consumable: unlimited energy + ad-free
 *   - `no_ads_forever`    — non-consumable: suppresses interstitial ads
 *                            permanently. Rewarded ads still show. Does
 *                            NOT touch `noAdsExpires` (the time-bounded
 *                            flag set by `unlimited_24h`); instead sets
 *                            `User.noAdsForever = true`.
 *   - `starter_pack`      — first-purchase-only consumable bundle.
 *                            Grants 200💎 + 500🪙 + 5⚡ + Founder frame
 *                            cosmetic. Server REJECTS the purchase if
 *                            `User.totalIapPurchases > 0` BEFORE granting
 *                            (`IAP_STARTER_PACK_ALREADY_PURCHASED`). The
 *                            extra fields (`coins`, `energy`,
 *                            `cosmeticFrame`) are inert for non-bundle
 *                            SKUs — the IAP grant pipeline switches on
 *                            `productId` and ignores fields that don't
 *                            apply to the routed branch.
 */
export const GEM_IAP_PRODUCTS = [
  { productId: 'gems_starter', gems: 80, priceUsd: 0.99, bestValue: false },
  { productId: 'gems_small', gems: 280, priceUsd: 2.99, bestValue: false },
  { productId: 'gems_medium', gems: 500, priceUsd: 4.99, bestValue: true },
  { productId: 'gems_large', gems: 1100, priceUsd: 9.99, bestValue: false },
  { productId: 'gems_mega', gems: 2400, priceUsd: 19.99, bestValue: false },
  { productId: 'gems_titan', gems: 6500, priceUsd: 49.99, bestValue: false },
  { productId: 'unlimited_24h', gems: 0, priceUsd: 4.99, bestValue: false },
  // Non-consumable. Sets User.noAdsForever = true.
  { productId: 'no_ads_forever', gems: 0, priceUsd: 4.99, bestValue: false },
  // First-purchase bundle. See doc comment above for the full grant shape.
  {
    productId: 'starter_pack',
    gems: 200,
    coins: 500,
    energy: 5,
    cosmeticFrame: 'founder',
    priceUsd: 1.99,
    bestValue: false,
  },
] as const;

/**
 * Composition of the `starter_pack` first-purchase bundle. Sourced from
 * the catalog above so the bundle constant and the catalog stay in sync
 * — drift between the two would be a release blocker (server credits
 * the catalog amount; client renders this constant in the banner UI).
 */
export const STARTER_PACK = {
  PRODUCT_ID: 'starter_pack',
  GEMS: 200,
  COINS: 500,
  ENERGY: 5,
  COSMETIC_FRAME: 'founder',
  PRICE_USD: 1.99,
  /** Days to suppress the banner after a user dismisses it. */
  BANNER_SUPPRESS_DAYS: 7,
} as const;

/**
 * Restore Purchases (#409, App Store Review 3.1.1).
 *
 * `MAX_RECEIPTS_PER_BATCH` caps a single POST /shop/restore call so a
 * malicious or pathological client cannot fan out into hundreds of
 * verifier round-trips. Real-world receipt counts are 1–5 (one per
 * non-consumable owned, plus any historical cancelled subscriptions
 * the store still surfaces); 50 is far above the worst-case ceiling.
 */
export const IAP_RESTORE = {
  MAX_RECEIPTS_PER_BATCH: 50,
} as const;

/**
 * Refund webhook policy (`docs/ECONOMY.md` §6.4 — D6, D7).
 *
 * `REVIEW_FLAG_THRESHOLD` is the inclusive upper bound on refunded
 * transactions in the trailing `REVIEW_WINDOW_DAYS` window before the
 * server flips `User.refundReviewFlag` for manual admin review. Today's
 * threshold is "more than 2 in 30 days" — i.e. the third refund in the
 * window flips the flag. No automated ban or grant freeze in v1.
 */
export const REFUND = {
  REVIEW_FLAG_THRESHOLD: 2,
  REVIEW_WINDOW_DAYS: 30,
} as const;

/**
 * Rewarded-ad placements (#416): the wire contract between the mobile client
 * and the server-side reward routing. The client threads the placement to the
 * network's signed reward callback (AppLovin `{PLACEMENT}` macro / AdMob
 * `custom_data` as `<userId>|<placement>`), and the callbacks store it as the
 * verified reward type consumed by `/shop/ad-reward` ('energy'/'coins') and
 * `/economy/match-reward-doubler` ('match_doubler'). One rewarded ad unit
 * serves ALL placements, so the network-configured reward item alone cannot
 * route — the placement must ride the signed payload (the #7251bb7 incident).
 */
export const AD_PLACEMENTS = ['energy', 'coins', 'match_doubler'] as const;
export type AdPlacement = (typeof AD_PLACEMENTS)[number];

export const ADS = {
  /** Show interstitial ad between matches */
  INTERSTITIAL_BETWEEN_MATCHES: true,
  /** Show interstitial every N matches (e.g. 3 = every 3rd match) */
  INTERSTITIAL_FREQUENCY: 3,
  /** Close button delay for interstitial (seconds) */
  INTERSTITIAL_CLOSE_DELAY_SECONDS: 5,
  /** Hours the "No Ads" purchase lasts */
  NO_ADS_DURATION_HOURS: 24,
  /** Hours the unlimited energy purchase lasts */
  UNLIMITED_ENERGY_DURATION_HOURS: 24,
  /**
   * SSV claim retry: the network's server-side reward callback (AppLovin S2S /
   * AdMob SSV) can lag the client's ad-close event by several seconds, so the
   * first claim can race it and 403 as not-verified. The client retries the
   * claim on the not-verified codes with linear backoff
   * (BASE_MS × attempt: 1s, 2s, 3s, 4s ≈ 10s total) before surfacing an error.
   */
  SSV_CLAIM_MAX_ATTEMPTS: 5,
  SSV_CLAIM_BACKOFF_BASE_MS: 1000,
} as const;
